import { isBlank } from './stringFormat';

export const decimalFormatter = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

export const integerFormatter = new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 0
});

export const compactIntegerFormatter = new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 0,
    notation: 'compact'
});

export const oneDecimalFormatter = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
});

export const percentRatioFormatter = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    style: 'percent'
});

export const percentRatioTwoDecimalsFormatter = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'percent'
});

export const shortDecimalFormatter = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1
});

export const formatDecimal = (value) => decimalFormatter.format(Number(value));

export const formatInteger = (value) => integerFormatter.format(Number(value));

export const formatCompactInteger = (value) => compactIntegerFormatter.format(Number(value));

export const formatOneDecimal = (value) => oneDecimalFormatter.format(Number(value));

// Percentuais são exibidos com a mesma precisão de uma casa decimal.
export const formatPercentage = formatOneDecimal;

export const formatPercentRatio = (value) => percentRatioFormatter.format(Number(value));

export const formatPercentRatioTwoDecimals = (value) => percentRatioTwoDecimalsFormatter.format(Number(value));

export const formatFunnelPercent = (value, total) => (
    total > 0 ? formatPercentage((Number(value) / Number(total)) * 100) : formatPercentage(0)
);

export const percent = (value, total) => (
    total > 0 ? Math.round((value / total) * 100) : 0
);

export const ratio = (value, total) => (total > 0 ? value / total : 0);

const NUMERIC_TEXT_NOISE = /[^\d,.-]/g;
const GROUPED_THOUSANDS_TEXT = /^-?\d{1,3}(\.\d{3})+$/;

// Valores em pt-BR ("1.234,56"): ponto separa milhar e vírgula é o decimal.
export const normalizeNumberTextPtBr = (value) => String(value ?? '')
    .replace(NUMERIC_TEXT_NOISE, '')
    .replace(/\./g, '')
    .replace(',', '.');

export const parseNumberPtBr = (value, fallback = 0) => {
    if (typeof value === 'number') return Number.isNaN(value) ? fallback : value;
    if (isBlank(value)) return fallback;

    const normalizedValue = Number(normalizeNumberTextPtBr(value));

    return Number.isNaN(normalizedValue) ? fallback : normalizedValue;
};

export const parseNumberPtBrOrThrow = (value, errorMessage) => {
    const parsedValue = parseNumberPtBr(value, NaN);

    if (Number.isNaN(parsedValue)) {
        throw new Error(errorMessage);
    }

    return parsedValue;
};

// Texto digitado ou colado pelo usuário: o separador decimal vem do próprio texto.
export const normalizeNumericText = (value) => {
    const sanitizedValue = String(value ?? '').replace(NUMERIC_TEXT_NOISE, '');

    if (sanitizedValue.includes(',')) {
        return sanitizedValue.replace(/\./g, '').replace(',', '.');
    }

    if (GROUPED_THOUSANDS_TEXT.test(sanitizedValue)) {
        return sanitizedValue.replace(/\./g, '');
    }

    return sanitizedValue;
};

export const parseNumericText = (value, fallback = 0) => {
    if (typeof value === 'number') return Number.isNaN(value) ? fallback : value;

    const normalizedValue = normalizeNumericText(value);
    if (normalizedValue === '') return fallback;

    const parsedValue = Number(normalizedValue);

    return Number.isNaN(parsedValue) ? fallback : parsedValue;
};

export const HOURS_PER_DAY = 24;

export const formatDurationHours = (hours) => {
    if (isBlank(hours)) return null;

    const numericHours = Number(hours);
    if (Number.isNaN(numericHours) || numericHours < 0) return null;
    if (numericHours < 1) return `${Math.round(numericHours * 60)} min`;
    if (numericHours < HOURS_PER_DAY) return `${shortDecimalFormatter.format(numericHours)} h`;

    return `${shortDecimalFormatter.format(numericHours / HOURS_PER_DAY)} d`;
};

const BYTES_PER_KILOBYTE = 1024;
const BYTES_PER_MEGABYTE = BYTES_PER_KILOBYTE * BYTES_PER_KILOBYTE;

export const formatMegabytes = (bytes) => shortDecimalFormatter.format(bytes / BYTES_PER_MEGABYTE);

export const formatFileSize = (bytes) => (
    bytes >= BYTES_PER_MEGABYTE
        ? `${formatMegabytes(bytes)} MB`
        : `${Math.max(1, Math.round(bytes / BYTES_PER_KILOBYTE))} KB`
);

// Teto "redondo" para eixos de gráfico (10, 20, 50, 100, ...).
export const getNiceMax = (value) => {
    if (value <= 10) return 10;

    const magnitude = 10 ** Math.floor(Math.log10(value));
    const normalizedValue = value / magnitude;
    const niceNormalizedValue = normalizedValue <= 2 ? 2 : normalizedValue <= 5 ? 5 : 10;

    return niceNormalizedValue * magnitude;
};
